import {
  Button as ChakraUIButton,
  ButtonGroup as ChakraButtonGroup,
  type ButtonProps as ChakraButtonProps,
  type ButtonGroupProps as ChakraButtonGroupProps
} from "@chakra-ui/react";
import {
  Children,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  type ForwardedRef
} from "react";

type CustomButtonVariant =
  | ChakraButtonProps["variant"]
  | "primary"
  | "secondary"
  | "tertiary"
  | "link"
  | "emoji-row"
  | "emoji-column"
  | "destructive-primary"
  | "destructive-secondary"
  | "destructive-tertiary"
  | "destructive-link";

interface IButtonProps extends Omit<ChakraButtonProps, "variant" | "size"> {
  destructive?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  variant?: CustomButtonVariant | string;
  size?:
    | ChakraButtonProps["size"]
    | string
    | Record<string, string>
    | Array<string | null>;
  children?: ReactNode;
}

const Button = forwardRef(function Button(
  {
    children,
    leftIcon,
    rightIcon,
    destructive,
    variant,
    ...props
  }: IButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const resolvedVariant = (
    destructive && variant ? `destructive-${variant}` : variant
  ) as ChakraButtonProps["variant"];

  const wrappedChildren =
    typeof children === "string" || typeof children === "number" ? (
      <span>{children}</span>
    ) : (
      children
    );

  return (
    <ChakraUIButton
      ref={ref}
      variant={resolvedVariant}
      {...(props as ChakraButtonProps)}
    >
      {leftIcon}
      {wrappedChildren}
      {rightIcon}
    </ChakraUIButton>
  );
});

export type ButtonGroupCompatProps = Omit<
  ChakraButtonGroupProps,
  "size" | "disabled"
> & {
  variant?: ChakraButtonGroupProps["variant"] | string;
  disabled?: boolean;
  size?:
    | ChakraButtonGroupProps["size"]
    | string
    | Record<string, string>
    | Array<string | null>;
};

function ButtonGroup({
  variant,
  disabled,
  size,
  children,
  ...props
}: ButtonGroupCompatProps) {
  const enhancedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;

    return cloneElement(child, {
      ...(disabled ? { disabled: true } : {}),
      ...(size ? { size } : {})
    });
  });

  return (
    <ChakraButtonGroup
      variant={variant as ChakraButtonGroupProps["variant"]}
      size={size as ChakraButtonGroupProps["size"]}
      {...(props as ChakraButtonGroupProps)}
    >
      {enhancedChildren}
    </ChakraButtonGroup>
  );
}

export { Button, ButtonGroup, type IButtonProps };
